import USIcon from "@assets/icons/us_26px.svg?react";
import EUIcon from "@assets/icons/eu_26px.svg?react";
import { runtimeConfig } from "@src/runtime-config";
import { IconButton, Tooltip } from "@flat-ui";
import { SvgIcon } from "@mui/material";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { NavbarEvents } from "@utils/track-event/track-event-list";
import { useAppParams } from "@router/router-helper";
import { useAppSelector } from "@store/store-helper";
import { projectStateSelector } from "@store/project/project-slice";
import { isCompanyContext } from "@utils/type-guards";
import { getSphereLink, SphereLink } from "@utils/url-utils";

/**
 * @returns The link to the workspace or project open in the AdminPanel in Sphere,
 * or to the Sphere workspace list if neither is open
 */
function useSphereLink(): SphereLink {
  const { companyId, projectId } = useAppParams();
  const { project } = useAppSelector(projectStateSelector);

  // On a project page the workspace is only known once the project is loaded
  const projectCompanyId =
    project?.id === projectId && project && isCompanyContext(project.context)
      ? project.context.company.id
      : undefined;

  return getSphereLink({
    companyId: companyId ?? projectCompanyId,
    projectId,
  });
}

/**
 * Component to show a button in the navbar with an icon representing the app region (EU or US)
 * If clicked it opens the workspace or project open in the AdminPanel in Sphere of the current app region,
 * or the Sphere workspace list if neither is open.
 * On hover it shows a tooltip the full name of the app region.
 */
export function AppRegionButton(): React.JSX.Element {
  const { trackEvent } = useTrackEvent();
  const sphereLink = useSphereLink();
  const appEnvIcon = runtimeConfig.appRegion === "eu" ? EUIcon : USIcon;

  return (
    <Tooltip title={runtimeConfig.displayAppEnv}>
      <IconButton
        href={sphereLink.url}
        onClick={() =>
          trackEvent({
            name: NavbarEvents.navigateToWorkspaceList,
            props: { target: sphereLink.target },
          })
        }
      >
        <SvgIcon component={appEnvIcon} inheritViewBox />
      </IconButton>
    </Tooltip>
  );
}
